<script setup lang="ts">
/**
 * The dashed „+ Neu …“ tile at the end of an `NxTileGrid`: a click turns it
 * into a name field in place. Enter (or the create button) creates, Escape
 * (or the cancel button) goes back; leaving the empty field goes back too.
 *
 * `validate` refuses a value before anything happens (an error text under the
 * field). `save` stores it (a request to the server): the field stays, read-only
 * and busy, until it settles, and a refusal — an error text returned or thrown
 * („Den Namen gibt es schon“) — keeps it open with that error. After a
 * successful save (or right away without `save`) the tile emits `commit` and
 * goes back to the „+“ state. `v-model:editing` opens and closes it from
 * outside.
 */
import Plus from 'lucide-vue-next/dist/esm/icons/plus.js';
import { inject, nextTick, ref, useId, watch } from 'vue';
import NxButton from '../Button/NxButton.vue';
import { tileGridKey } from './context';

const props = withDefaults(
  defineProps<{
    /** The tile's caption and the field's label („Neues Profil“). */
    label: string;
    placeholder?: string;
    /** A line under the field („Enter legt an, Escape bricht ab“). */
    hint?: string;
    submitLabel?: string;
    cancelLabel?: string;
    maxlength?: number;
    disabled?: boolean;
    /** Returns an error text for a value that may not be created. */
    validate?: (value: string) => string | null | undefined;
    /** Creates it; resolves to an error text (or throws) when that was refused. */
    save?: (value: string) => Promise<string | null | undefined | void>;
  }>(),
  {
    placeholder: '',
    hint: undefined,
    submitLabel: 'Anlegen',
    cancelLabel: 'Abbrechen',
    maxlength: undefined,
    disabled: false,
    validate: undefined,
    save: undefined,
  }
);

const emit = defineEmits<{
  /** A value was created (after `save`, if any). */
  commit: [value: string];
  /** The field closed without creating anything. */
  cancel: [];
}>();

const editing = defineModel<boolean>('editing', { default: false });

const inGrid = inject(tileGridKey, false);
const draft = ref('');
const error = ref<string | null>(null);
const saving = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);
const buttonRef = ref<HTMLButtonElement | null>(null);
const formRef = ref<HTMLFormElement | null>(null);
const inputId = useId();
const noteId = useId();

watch(
  editing,
  async (open) => {
    if (!open) return;
    draft.value = '';
    error.value = null;
    await nextTick();
    inputRef.value?.focus();
  },
  { immediate: true }
);

function start() {
  if (props.disabled) return;
  editing.value = true;
}

/** Back to the „+“ state; `refocus` hands the focus to it (keyboard). */
async function close(refocus: boolean) {
  editing.value = false;
  error.value = null;
  if (!refocus) return;
  await nextTick();
  buttonRef.value?.focus();
}

function cancel(refocus = true) {
  if (saving.value) return;
  emit('cancel');
  void close(refocus);
}

async function submit() {
  if (saving.value) return;
  const value = draft.value.trim();
  const refusal = props.validate?.(value);
  if (refusal) {
    error.value = refusal;
    inputRef.value?.focus();
    return;
  }
  if (!value) {
    inputRef.value?.focus();
    return;
  }
  if (props.save) {
    error.value = null;
    saving.value = true;
    let saveRefusal: string | null | undefined | void;
    try {
      saveRefusal = await props.save(value);
    } catch (err) {
      saveRefusal = err instanceof Error ? err.message : String(err);
    } finally {
      saving.value = false;
    }
    if (saveRefusal) {
      error.value = saveRefusal;
      await nextTick();
      inputRef.value?.focus();
      return;
    }
  }
  emit('commit', value);
  void close(true);
}

function onKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || event.isComposing) return;
  // An open dialog around the tile must not close with it.
  event.preventDefault();
  event.stopPropagation();
  cancel();
}

/** Leaving the form with nothing typed goes back to the „+“ state. */
function onFocusOut(event: FocusEvent) {
  const next = event.relatedTarget as Node | null;
  if (next && formRef.value?.contains(next)) return;
  if (saving.value || draft.value.trim()) return;
  cancel(false);
}

defineExpose({ start, cancel });
</script>

<template>
  <div
    class="nx-add-tile"
    :class="{ 'is-editing': editing, 'is-disabled': props.disabled }"
    :role="inGrid ? 'listitem' : undefined"
    data-testid="nx-add-tile"
  >
    <button
      v-if="!editing"
      ref="buttonRef"
      type="button"
      class="nx-add-tile__button"
      :disabled="props.disabled"
      data-testid="nx-add-tile-button"
      @click="start"
    >
      <span class="nx-add-tile__plus" aria-hidden="true">
        <Plus class="nxt-icon-md" />
      </span>
      <span class="nx-add-tile__caption">{{ props.label }}</span>
    </button>
    <form
      v-else
      ref="formRef"
      class="nx-add-tile__form"
      novalidate
      @submit.prevent="submit"
      @keydown="onKeydown"
      @focusout="onFocusOut"
    >
      <label :for="inputId" class="nx-add-tile__label">{{ props.label }}</label>
      <input
        :id="inputId"
        ref="inputRef"
        v-model="draft"
        class="nx-add-tile__input"
        :class="{ 'is-invalid': error }"
        type="text"
        autocomplete="off"
        :placeholder="props.placeholder"
        :maxlength="props.maxlength"
        :readonly="saving"
        :aria-busy="saving || undefined"
        :aria-invalid="error ? 'true' : undefined"
        :aria-describedby="error || props.hint ? noteId : undefined"
        data-testid="nx-add-tile-input"
        @input="error = null"
      />
      <p v-if="error" :id="noteId" class="nx-add-tile__error" role="alert">{{ error }}</p>
      <p v-else-if="props.hint" :id="noteId" class="nx-add-tile__hint">{{ props.hint }}</p>
      <div class="nx-add-tile__actions">
        <NxButton height="xs" type="submit" :loading="saving" data-testid="nx-add-tile-submit">
          {{ props.submitLabel }}
        </NxButton>
        <NxButton
          height="xs"
          type="button"
          variant="ghost"
          :disabled="saving"
          data-testid="nx-add-tile-cancel"
          @click="cancel()"
        >
          {{ props.cancelLabel }}
        </NxButton>
      </div>
    </form>
  </div>
</template>

<style scoped>
.nx-add-tile {
  display: flex;
  min-width: 0;
  min-height: calc(var(--nxt-control-size-lg) * 2.5);
  border: calc(var(--nxt-hairline) * 1.5) dashed var(--nxt-border-strong);
  border-radius: var(--nxt-radius-lg);
  transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-add-tile:hover:not(.is-disabled, .is-editing) {
  border-color: var(--nxt-brand-border);
  background: color-mix(in srgb, var(--nxt-brand-subtle-bg) 40%, transparent);
}
.nx-add-tile.is-editing {
  border-style: solid;
  border-color: var(--nxt-brand-border);
  background: var(--nxt-surface-raised-bg);
}

.nx-add-tile__button {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-sm);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-muted);
  background: none;
  border: none;
  border-radius: inherit;
  cursor: pointer;
  outline: none;

  &:hover:not(:disabled) {
    color: var(--nxt-brand-subtle-fg);
  }

  &:focus-visible {
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
}
.nx-add-tile__plus {
  display: grid;
  place-items: center;
  width: var(--nxt-control-size-lg);
  height: var(--nxt-control-size-lg);
  border-radius: var(--nxt-radius-full);
  color: var(--nxt-brand-subtle-fg);
  background: var(--nxt-brand-subtle-bg);
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-add-tile__button:hover:not(:disabled) .nx-add-tile__plus {
  color: var(--nxt-brand-fg);
  background: var(--nxt-brand-bg);
}

.nx-add-tile__form {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  min-width: 0;
  padding: var(--nxt-space-sm) var(--nxt-space-sm) var(--nxt-space-sm) var(--nxt-space-md);
}
.nx-add-tile__label {
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-muted);
}
.nx-add-tile__input {
  width: 100%;
  min-width: 0;
  height: var(--nxt-control-size-md);
  margin: 0;
  padding: 0 var(--nxt-space-xs);
  font: inherit;
  font-size: var(--nxt-input-size);
  color: var(--nxt-input-fg);
  background: var(--nxt-input-bg);
  border: var(--nxt-hairline) solid var(--nxt-input-border);
  border-radius: var(--nxt-radius-md);
  outline: none;

  &::placeholder {
    color: var(--nxt-input-placeholder);
  }

  &:focus {
    background: var(--nxt-input-bg-focus);
    border-color: var(--nxt-input-border-focus);
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring);
  }

  &.is-invalid {
    border-color: var(--nxt-input-border-invalid);
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring-invalid);
  }

  &[readonly] {
    color: var(--nxt-fg-muted);
    cursor: progress;
  }
}
.nx-add-tile__hint,
.nx-add-tile__error {
  margin: 0;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-regular);
  line-height: var(--nxt-line-height-tight);
}
.nx-add-tile__hint {
  color: var(--nxt-fg-subtle);
}
.nx-add-tile__error {
  color: var(--nxt-danger-subtle-fg);
}
.nx-add-tile__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-xs);
  margin-block-start: auto;
}

@media (prefers-reduced-motion: reduce) {
  .nx-add-tile,
  .nx-add-tile__plus {
    transition: none;
  }
}
</style>
